import { useEffect, useState, useCallback } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import styled from 'styled-components';
import auth from './api/auth';
// Components
import Loading from './components/Loading/Loading'
import Navbar from './components/Navbar/Navbar';

import Login from './containers/Authentication/Login';
import ResetPassword from './containers/Authentication/ResetPassword';
import ResetPasswordConfirm from './containers/Authentication/ResetPasswordConfirm';
import StudyDetails from './containers/StudyDetails/StudyDetails';
import StudyList from './containers/StudyList/StudyList';
import GlobalRegistries from './containers/GlobalRegistries/GlobalRegistries';
import Interventions from './containers/Interventions/Interventions';
import Dashboard from './containers/Dashboard/Dashboard';
import Analytics from './containers/Analytics/Analytics';
import Conditions from './containers/Conditions/Conditions';
import Attachments from './containers/Attachments/Attachments';
import Locations from './containers/Locations/Locations';
import Registries from './containers/Registries/Registries';
import AnalyticsDashboard from './containers/Analytics/DjangoAnalytics/AnalyticsDashboard';
import Users from './containers/Users/Users';
import AllCTGList from './containers/AllCTGList/AllCTGList'
import NotFound from './containers/NotFound/NotFound';
import CTGStudyDetails from './containers/CTGStudyDetails/CTGStudyDetails';
import 'react-toastify/dist/ReactToastify.css'
import Clients from './containers/Clients/Clients';
import USERTYPES from './constants/usertypes';
import Languages from './containers/Languages/Languages';
import CommercialProducts from './containers/CommercialProduct/CommercialProducts';
import DevelopmentStatuss from './containers/DevelopmentStatus/DevelopmentStatus';
import Glossary from './containers/Glossary/Glossary';
import { getCookie } from './api/helpers';
import { isExpired } from "react-jwt"
import { getUserInfo } from './api/auth-api';
import { getClientAdditionalInfo } from './api/clients';
import { checkTenantAndHandle } from './helpers/tokenHelper';
import useRefreshToken from './containers/Authentication/useRefreshToken';
import { useTenantConfig } from './context/TenantConfigContext';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';

const App = () => {
  const [isAuth, setAuth] = useState(null);
  const [usertype, setUsertype] = useState(null);
  const { updateConfig } = useTenantConfig()
  
  // Logout function to be used by inactivity timeout
  const handleInactivityLogout = useCallback(() => {
    setAuth(false);
    setUsertype(null);
  }, []);
  
  // Initialize token refresh functionality
  useRefreshToken(handleInactivityLogout);
  // const [user, setUser] = useState(
  //   {
  //     first_name: '',
  //     last_name: '',
  //     permissions: ['user.edit', 'user.create']
  //   }
  // );

  useEffect(() => {
    checkTenantAndHandle();
  }, []);

  useEffect(() => {
      let accessToken = getCookie('access')
      if (accessToken) {
        if (!isExpired(accessToken)){
          setAuth(true)
        }
        else{
          setAuth(false)
        }
      }
      else{
        setAuth(false)
      }
      return null
  }, []
  );

  useEffect(() => {
    if(isAuth){
      getUserInfo().then(res => {
        // set
        localStorage.setItem('disableAnalytics', res.data?.disableAnalytics || false)
        localStorage.setItem('only_attachment', res.data?.only_attachment ? "true" : "false")
        setUsertype(res.data?.usertype)

        // Update config with user info (this syncs to localStorage via the context)
        updateConfig({
          username: res.data?.username,
          client: res.data?.client,
        })

        // Immediately apply legacy disableAnalytics as fallback
        if (res.data?.disableAnalytics) {
          updateConfig({ disabledPages: ['analytics'] })
        }

        // Fetch tenant config for disabled pages
        const clientName = res.data?.client
        if (clientName) {
          getClientAdditionalInfo(clientName).then(additionalInfo => {
            const disabledPages = additionalInfo?.disabledPages || []
            // Merge legacy disableAnalytics flag
            if (res.data?.disableAnalytics && !disabledPages.includes('analytics')) {
              disabledPages.push('analytics')
            }
            const enableStudyPageImage = additionalInfo?.enableStudyPageImage || false
            updateConfig({ disabledPages, enableStudyPageImage })
          }).catch(() => {
            // If additional info fetch fails, keep the legacy fallback already set above
          })
        }
      })
    }

  }, [isAuth])

  const renderAuthenticatedView = () => {
    return (
      <Routes>
        <Route exact path="/:client" element={<Dashboard usertype={usertype}/>} />
        <Route exact path="/:client/studies" element={<StudyList />} />
        <Route exact path="/:client/global-registries" element={<GlobalRegistries />} />
        <Route exact path="/:client/interventions" element={<Interventions />} />
        <Route exact path="/:client/studies/:id" element={<StudyDetails />} />
        <Route exact path="/:client/conditions" element={<Conditions />} />
        <Route exact path="/:client/attachments" element={<Attachments />} />
        <Route exact path="/:client/locations" element={<Locations />} />
        <Route exact path="/:client/study-registries" element={<Registries />} />
        <Route exact path="/:client/registries" element={<Registries />} />
        <Route exact path="/:client/analytics" element={<ProtectedRoute pageKey="analytics"><Analytics /></ProtectedRoute>} />
        { usertype === USERTYPES.super_admin &&
          <Route exact path="/:client/users" element={<Users />} />
        }
        <Route exact path="/:client/analytics-dashboard" element={<AnalyticsDashboard />} />
        {(usertype === USERTYPES.super_admin && (localStorage.getItem('client') === 'public' || localStorage.getItem('client') === 'xogene')) &&
          <Route exact path="/:client/clients" element={<Clients />} />
        }

        {/* <Route exact path="/:client/ctg-studies" element={<AllCTGList />} /> */}
        {/* <Route exact path="/:client/ctg-studies/:id" element={<CTGStudyDetails />} /> */}
        <Route exact path="/:client/languages" element={<Languages />} />
        <Route exact path="/:client/commercial-products" element={<ProtectedRoute pageKey="commercial-products"><CommercialProducts /></ProtectedRoute>} />
        <Route exact path="/:client/development-status" element={<ProtectedRoute pageKey="development-status"><DevelopmentStatuss /></ProtectedRoute>} />
        <Route exact path="/:client/glossary" element={<ProtectedRoute pageKey="glossary"><Glossary /></ProtectedRoute>} />
                {/* <Route exact path="/:client/global-registries" element={<GlobalRegistries />} /> */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    )
  }

  const renderNonAuthenticatedView = () => {
    return (
      <Routes>
        <Route exact path="/:client" element={
          <Login
            isAuth={isAuth}
            setAuth={setAuth}
            setUsertype={setUsertype}
            // setUser={setUser}
          />
          }/>

        <Route exact path="/:client/forgot-password"
          element={ <ResetPassword isAuth={isAuth}/> }/>

        <Route exact path="/:client/password-reset"
          element={<ResetPasswordConfirm isAuth={isAuth}/>}/>

        <Route path="*" element={<NotFound />} />
      </Routes>
    )
  }

  return (
    <Container>
      <Navbar
        // user={user}
        // setUser={setUser}
        isAuth={isAuth}
        setAuth={setAuth}
        usertype={usertype} />

      <ContentContainer>
        <RouteContainer>
          {isAuth === null ? <Loading /> :
            isAuth === true ?
              renderAuthenticatedView() :
              renderNonAuthenticatedView()
          }
        </RouteContainer>
      </ContentContainer>

      <ToastContainer />
    </Container>
  );
}

export default App;

const Container = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;
  background-color: #f5f7fa;
`

const ContentContainer = styled.div`
  flex: 1;
  min-width: 0;
  height: 100vh;
  overflow-y: auto;
  overscroll-behavior: contain;
`

const RouteContainer = styled.div`
  width: 100%;
  height: 100%;
`
